/**
 * The denial reasons `DeviceService` returns for a `403` on `/api/devices/v2026.6/configuration` (#269).
 *
 * Before #269 those `403`s had an **empty body**, so the editor could only say "Keine Berechtigung für diese
 * Aktion." — which is both true and useless: it does not distinguish a *product rule* (a Standort-Administrator
 * may not write the company-wide baseline) from a broken session or a genuine role-mapping defect. Diagnosing the
 * reported case meant reading the DeviceService container log.
 *
 * The backend now answers the same structured `ApiError` body the monolith already uses —
 * `{ code, description, statusCode }` — so this module can branch on the **stable code** exactly like
 * `orders.component.ts` does for the storno guards.
 *
 * Note what this deliberately is **not**: it does not gate, hide or disable anything. The editor still offers the
 * company baseline to a Standort-Administrator and the save still fails — it just stops being mysterious. Gating
 * the UI is tracked separately.
 */

/**
 * The codes the configuration endpoints can deny with. Mirrors
 * `CheqMe.Server.Services.DeviceService/Configuration/DeviceConfigurationDenial.cs` — the backend is the source of
 * truth, this is the consumer's copy of the contract.
 */
export const DeviceConfigDenialCode = {
  /** The session carries no company — a broken/stale token rather than a permission problem. */
  companyClaimMissing: 'E_DEVICE_CONFIG_COMPANY_CLAIM_MISSING',
  /** A register asked for another device's configuration. Not reachable from the web editor. */
  ownDeviceOnly: 'E_DEVICE_CONFIG_OWN_DEVICE_ONLY',
  /** The self endpoint was called by a token that is not a device. Not reachable from the web editor. */
  clientIdClaimMissing: 'E_DEVICE_CONFIG_CLIENT_ID_CLAIM_MISSING',
  /** The targeted layer belongs to another Standort than the caller administers. */
  shardScope: 'E_DEVICE_CONFIG_SHARD_SCOPE',
  /** **The reported case:** writing the company-wide baseline needs a company-wide role. */
  companyWideWrite: 'E_DEVICE_CONFIG_COMPANY_WIDE_WRITE',
  /** No Standort could be resolved (neither supplied nor carried by the session). */
  shardUnresolvable: 'E_DEVICE_CONFIG_SHARD_UNRESOLVABLE'
} as const;

/** The German text shown for each code. The server description is English and only a last-resort fallback. */
const MESSAGES: Readonly<Record<string, string>> = {
  [DeviceConfigDenialCode.companyClaimMissing]:
    'Ihre Sitzung ist keinem Unternehmen zugeordnet. Bitte melden Sie sich erneut an.',
  [DeviceConfigDenialCode.ownDeviceOnly]:
    'Ein Kassengerät darf nur seine eigene Konfiguration lesen.',
  [DeviceConfigDenialCode.clientIdClaimMissing]:
    'Diese Sitzung gehört zu keinem registrierten Gerät.',
  [DeviceConfigDenialCode.shardScope]:
    'Diese Ebene gehört zu einem anderen Standort als dem, für den Sie berechtigt sind.',
  [DeviceConfigDenialCode.companyWideWrite]:
    'Die unternehmensweite Grundeinstellung darf nur von einer unternehmensweiten Rolle gespeichert werden. '
    + 'Als Standort-Administrator können Sie die Grundeinstellung Ihres Standorts speichern.',
  [DeviceConfigDenialCode.shardUnresolvable]:
    'Es konnte kein Standort ermittelt werden: es wurde keiner ausgewählt und Ihre Sitzung enthält keinen.'
};

/**
 * The German text for a `403` this build has no sentence for — a reason added to the backend since it shipped,
 * a body with no code at all, or a gateway-level refusal that carries nothing structured.
 *
 * **Do not make it more specific, and do not widen the resolver back to `description`.** That field is
 * `ErrorCatalog` text, developer-facing English by design (frontend style guide, *"Surfacing an API error —
 * branch on `code`, never render `description`"*). Reaching for it here was defended as "a new denial reason
 * must still reach the user", which is true and is answered by mapping the code above — not by putting the
 * server's English on a German screen, which is precisely how the shift plan shipped English into production
 * (#386/#392). If you land here from a real refusal, add its mapping to {@link MESSAGES}.
 */
export const GENERIC_DENIAL_MESSAGE = 'Keine Berechtigung für diese Aktion.';

/**
 * Resolves the message to show for a denied request.
 *
 * Two tiers: a **known code** becomes its German text above; everything else — an unknown code, no code, or no
 * structured body at all — becomes {@link GENERIC_DENIAL_MESSAGE}. There is deliberately no tier that reads
 * `description`.
 *
 * @param error The `HttpErrorResponse` (typed loosely: the callers hold it as an untyped rxjs error).
 */
export function deviceConfigDenialMessage(error: unknown): string {
  const body = (error as { error?: { code?: unknown } } | null)?.error;
  const code = typeof body?.code === 'string' ? body.code : null;

  return (code && MESSAGES[code]) || GENERIC_DENIAL_MESSAGE;
}
